import { calculateCheckoutChargeAmount } from "@lemmonhq/orders";
import type {
  CheckoutData,
  PaymentIntegrationConfig,
  PaymentIntegrationType,
  SessionPaymentIntegration,
  User,
} from "@lemmonhq/types";
import type { TrpcClient } from "@app/vue/providers/TrpcProvider";
import type { useNativeBridge } from "@app/vue/providers/NativeBridge";

type PayrexxSaleOptions = {
  amount: number;
  orderReference: string;
  paymentMethod?: "CARD" | "TWINT";
  showResult?: boolean;
};

type PayrexxTransaction = {
  id: string | null;
  status: "SUCCESSFUL" | "PENDING" | "FAILED" | null;
};

type PayrexxNormalizedResponse = {
  status?: string | null;
  responseCode?: string | null;
};

type PayrexxSoftposConfigDeps = {
  bridgeRpc: {
    request: {
      (
        method: "payrexxSale",
        params: PayrexxSaleOptions,
      ): Promise<PayrexxTransaction>;
      (
        method: "payrexxSaleStatus",
        params: { orderReference: string },
      ): Promise<PayrexxTransaction | null>;
    };
  } | null;
};

const paymentCache = new Map<string, PayrexxTransaction>();

/**
 * How fast the client polls `integratedPaymentWorkflow.getStatus`, keyed by the
 * durable-execution platform actually running the workflow (returned by
 * `create`). DBOS drives the payment row to its next state roughly every
 * second, Restate about every two, so polling faster than the platform moves
 * only burns requests.
 */
export const POLL_INTERVAL_MS_BY_WORKFLOW_PLATFORM: Record<
  NonNullable<CheckoutData["paymentIntegrationWorkflowPlatform"]>,
  number
> = {
  dbos: 1000,
  restate: 2000,
  cloudflare: 2000,
};

const parseObjectResponse = <T>(response: unknown): T | null => {
  if (!response) {
    return null;
  }

  if (typeof response === "string") {
    try {
      return JSON.parse(response) as T;
    } catch {
      return null;
    }
  }

  if (typeof response === "object") {
    return response as T;
  }

  return null;
};

const normalizePayrexxResponse: PaymentIntegrationConfig["normalizeIntegrationResponse"] =
  (response) => {
    const parsedResponse =
      parseObjectResponse<PayrexxNormalizedResponse>(response);
    const normalizedStatus = parsedResponse?.status?.toUpperCase();
    const responseCode = parsedResponse?.responseCode?.toUpperCase();

    if (normalizedStatus === "SUCCESSFUL" || responseCode === "SUCCESS") {
      return {
        status: "success",
        result: parsedResponse ?? response,
      };
    }

    if (
      normalizedStatus &&
      ["FAILED", "CANCELLED", "TERMINATED", "DECLINED", "ERROR"].includes(
        normalizedStatus,
      )
    ) {
      return {
        status: "failed",
        error: `Payrexx sale status: ${normalizedStatus}`,
        result: parsedResponse ?? response,
      };
    }

    if (
      responseCode &&
      ["FAILED", "CANCELLED", "TERMINATED", "DECLINED", "ERROR"].includes(
        responseCode,
      )
    ) {
      return {
        status: "failed",
        error: `Payrexx response code: ${responseCode}`,
        result: parsedResponse ?? response,
      };
    }

    return {
      status: "pending",
      result: parsedResponse ?? response,
    };
  };

const createPayrexxSoftposPaymentIntegrationConfig = ({
  bridgeRpc,
}: PayrexxSoftposConfigDeps): PaymentIntegrationConfig => ({
  type: "payrexx_softpos",
  buttonMode: "card-twint",
  supportedMethods: ["card", "twint"],
  normalizeIntegrationResponse: normalizePayrexxResponse,
  createPaymentTransaction: async (input) => {
    if (!bridgeRpc) {
      throw new Error("Payrexx SoftPOS requires the native bridge");
    }

    const sale = await bridgeRpc.request("payrexxSale", {
      amount: calculateCheckoutChargeAmount(input.checkout),
      orderReference: String(input.paymentId),
      paymentMethod: input.paymentMethod === "card" ? "CARD" : "TWINT",
      showResult: false,
    });

    if (!sale.id) {
      throw new Error("Payrexx SoftPOS did not return a transaction id");
    }

    paymentCache.set(input.paymentId.toString(), sale);
    return {
      providerId: String(sale.id),
      response: sale,
      status: sale.status === "SUCCESSFUL" ? "confirmed" : "sent",
    };
  },
  checkPaymentTransactionStatus: async (input) => {
    if (!bridgeRpc) {
      throw new Error("Payrexx SoftPOS requires the native bridge");
    }

    const sale = paymentCache.get(input.paymentId.toString());
    const normalizedResponse = normalizePayrexxResponse(sale);

    if (normalizedResponse.status === "success") {
      return {
        status: "success",
        paymentId: input.paymentId,
        result: normalizedResponse.result,
      };
    }

    if (normalizedResponse.status === "failed") {
      return {
        status: "failed",
        paymentId: input.paymentId,
        error: normalizedResponse.error,
      };
    }

    return {
      status: "pending",
      paymentId: input.paymentId,
    };
  },
});

const normalizeBackendWorkflowResponse =
  (
    provider: string,
    successStates: string[],
    failedStates: string[],
    authorizedStates: string[] = [],
  ): PaymentIntegrationConfig["normalizeIntegrationResponse"] =>
  (response) => {
    const parsed = parseObjectResponse<Record<string, any>>(response);
    if (!parsed) {
      return { status: "pending", result: response };
    }

    const status = String(
      parsed.status ||
        parsed.state ||
        parsed.payment_status ||
        parsed.result?.payment_status ||
        "",
    ).toUpperCase();

    const url = parsed.checkout_url || parsed.result?.checkout_url || undefined;

    if (authorizedStates.includes(status)) {
      return { status: "authorized", result: parsed, url };
    }

    if (successStates.includes(status)) {
      return { status: "success", result: parsed, url };
    }

    if (failedStates.includes(status)) {
      return {
        status: "failed",
        error: parsed.error || `${provider} payment status: ${status}`,
        result: parsed,
        url,
      };
    }

    return { status: "pending", result: parsed, url };
  };

const createBackendWorkflowPaymentIntegrationConfig = (
  type: Exclude<PaymentIntegrationType, "payrexx_softpos" | "payrexx">,
): PaymentIntegrationConfig => {
  if (type === "nakka") {
    return {
      type,
      buttonMode: "card-twint",
      supportedMethods: ["card", "twint"],
      pollIntervalMs: 3000,
      // Backend-driven: the workflow sends the sale to the terminal. Without
      // this, checkout attaches a placeholder providerId ("pending") and the
      // workflow, seeing a providerId, never sends the sale.
      integration: "backend",
      normalizeIntegrationResponse: normalizeBackendWorkflowResponse(
        "Nakka",
        ["SUCCESS"],
        ["TERMINATED", "FAILED"],
      ),
      createPaymentTransaction: async () => ({
        providerId: "pending",
        response: { pending: true },
        status: "sent",
      }),
      checkPaymentTransactionStatus: async (input) => ({
        status: "pending",
        paymentId: input.paymentId,
      }),
    };
  }

  if (type === "wallee") {
    return {
      type,
      buttonMode: "single-card",
      supportedMethods: ["card"],
      normalizeIntegrationResponse: normalizeBackendWorkflowResponse(
        "Wallee",
        ["SUCCESS", "AUTHORIZED", "COMPLETED", "FULFILL"],
        ["FAILED", "DECLINE", "DECLINED", "VOIDED"],
      ),
      createPaymentTransaction: async () => ({
        providerId: "pending",
        response: { pending: true },
        status: "sent",
      }),
      checkPaymentTransactionStatus: async (input) => ({
        status: "pending",
        paymentId: input.paymentId,
      }),
    };
  }

  return {
    type,
    buttonMode: "single-card",
    supportedMethods: ["card"],
    normalizeIntegrationResponse: normalizeBackendWorkflowResponse(
      "Tbilisi Bank",
      ["SUCCESS", "SUCCEEDED", "APPROVED"],
      ["FAILED", "DECLINED", "CANCELED", "CANCELLED"],
      ["AUTHORIZED", "AUTHORISED"],
    ),
    createPaymentTransaction: async () => ({
      providerId: "pending",
      response: { pending: true },
      status: "sent",
    }),
    checkPaymentTransactionStatus: async (input) => ({
      status: "pending",
      paymentId: input.paymentId,
    }),
  };
};

/**
 * Payrexx card payment on the cashier's paired terminal. Runs entirely in the
 * backend workflow (integration type `payrexx_terminal`): `integration` marks
 * it as backend-driven so checkout skips the on-device sale step.
 */
const createPayrexxTerminalPaymentIntegrationConfig =
  (): PaymentIntegrationConfig => ({
    type: "payrexx",
    buttonMode: "card-twint",
    supportedMethods: ["card", "twint"],
    pollIntervalMs: 2000,
    integration: "backend",
    normalizeIntegrationResponse: normalizeBackendWorkflowResponse(
      "Payrexx terminal",
      ["SUCCESS"],
      ["DECLINED", "UNDERPAID", "TERMINATED", "REVERTED", "EXPIRED", "FAILED"],
    ),
    createPaymentTransaction: async () => ({
      providerId: "pending",
      response: { pending: true },
      status: "sent",
    }),
    checkPaymentTransactionStatus: async (input) => ({
      status: "pending",
      paymentId: input.paymentId,
    }),
  });

// Whether a payment attempt is still the one the checkout is waiting on. Success,
// failure, closing the status modal, closing the checkout and starting a newer
// attempt all clear `txLoading` or change `paymentId`. `attemptPaymentId` is
// undefined when the attempt failed before its payment row was created.
export const isPaymentAttemptCurrent = (
  current: CheckoutData | null,
  attemptPaymentId: number | undefined,
): boolean =>
  !!current?.txLoading && current.paymentId === attemptPaymentId;

export type CheckoutIntegration = {
  integration: SessionPaymentIntegration;
  /** Sent as the workflow `integrationType`; may differ from integration.type. */
  integrationType: string;
  config: PaymentIntegrationConfig;
};

/**
 * The integrated payment the signed-in cashier should use, if any.
 *
 * 1. A paired Payrexx terminal assigned to the cashier wins: it is charged
 *    through its Payrexx integration as `payrexx_terminal`.
 * 2. Otherwise the first integration that lists the cashier in `user_ids`.
 * 3. Otherwise a Nakka terminal assigned to the cashier (paired through the
 *    legacy Laravel app), unless the restaurant has the `nakka_browser` kill
 *    switch on. Nakka reads its credentials from the terminal row, so no Nakka
 *    payment integration is needed: the terminal's guid stands in as the
 *    workflow's integration id.
 */
export const findCheckoutIntegration = (
  user: User | null | undefined,
  trpc: TrpcClient,
  bridge: ReturnType<typeof useNativeBridge>,
): CheckoutIntegration | null => {
  if (!user?.id) return null;
  const integrations = user.payment_integrations || [];
  const terminals = user.payment_terminals || [];

  const terminal = terminals.find(
    (t) => t.provider === "payrexx" && t.status === "paired" && t.integration_id,
  );
  const terminalIntegration =
    terminal &&
    integrations.find(
      (i) => i.id === terminal.integration_id && i.type === "payrexx",
    );
  if (terminalIntegration) {
    return {
      integration: terminalIntegration,
      integrationType: "payrexx_terminal",
      config: createPayrexxTerminalPaymentIntegrationConfig(),
    };
  }

  const integration = integrations.find((i) => i.user_ids.includes(user.id));
  const config =
    integration &&
    getCheckoutPaymentIntegrationConfig(integration.type, trpc, bridge);
  if (integration && config) {
    return { integration, integrationType: integration.type, config };
  }

  const nakkaTerminal =
    !user.restaurant?.feature_flags?.nakka_browser &&
    terminals.find(
      (t) =>
        (t.provider ?? "nakka") === "nakka" && t.status !== "not_connected",
    );
  if (nakkaTerminal) {
    return {
      integration: {
        id: nakkaTerminal.guid,
        type: "nakka",
        restaurant_id: user.restaurant_id,
        settings: { type: "terminal" },
        user_ids: [user.id],
        created_at: "",
        updated_at: "",
      } as SessionPaymentIntegration,
      integrationType: "nakka",
      config: createBackendWorkflowPaymentIntegrationConfig("nakka"),
    };
  }

  return null;
};

export const getCheckoutPaymentIntegrationConfig = (
  type: PaymentIntegrationType,
  _trpc: TrpcClient,
  bridge: ReturnType<typeof useNativeBridge>,
) => {
  if (type === "payrexx_softpos") {
    return createPayrexxSoftposPaymentIntegrationConfig({
      bridgeRpc: bridge?.rpc || null,
    }) satisfies PaymentIntegrationConfig;
  }

  if (type === "nakka") {
    return createBackendWorkflowPaymentIntegrationConfig(type);
  }

  if (type === "wallee") {
    return createBackendWorkflowPaymentIntegrationConfig(type);
  }

  if (type === "tiblisi_bank") {
    return createBackendWorkflowPaymentIntegrationConfig(type);
  }

  return null;
};
